import { useEffect, useState } from 'react'
import { Alert, Box, Button, FormControlLabel, MenuItem, Switch, TextField } from '@mui/material'
import type { ProjectAnalyticsConfig } from '@yourownmetric/shared'

import { useUpdateProjectConfigMutation } from '@/apis/opsApi'
import { useGetProjectConfigQuery } from '@/apis/v2Api'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { useProjectId } from '@/hooks/useReportArgs'
import { SectionCard } from '@/modules/reports/common'

import { withPrivacy } from './proofModel'

type Privacy = ProjectAnalyticsConfig['privacy']

/** Edits the project's `privacy` policy (the one the opt-out evidence reports on). */
export function PrivacySettingsCard() {
    const projectId = useProjectId()
    const dispatch = useAppDispatch()
    const { data } = useGetProjectConfigQuery(projectId)
    const [save, saveState] = useUpdateProjectConfigMutation()
    const [draft, setDraft] = useState<Privacy | null>(null)

    useEffect(() => {
        if (data) {
            setDraft(data.config.privacy)
        }
    }, [data])

    if (!data || !draft) {
        return null
    }
    const dirty = JSON.stringify(draft) !== JSON.stringify(data.config.privacy)
    const onSave = async () => {
        try {
            await save({ projectId, config: withPrivacy(data.config, draft) }).unwrap()
            dispatch(showNotification({ message: 'Privacy policy saved', severity: 'success' }))
        } catch {
            // shown from saveState.error
        }
    }
    const toggle = (k: 'scrubParams' | 'cookielessVisitorHash' | 'suppressForwardingOnOptOut') => (
        <Switch checked={draft[k]} onChange={e => setDraft({ ...draft, [k]: e.target.checked })} />
    )
    return (
        <SectionCard
            title="Privacy policy"
            action={
                <Button
                    variant="contained"
                    size="small"
                    disabled={!dirty || saveState.isLoading}
                    onClick={() => {
                        void onSave()
                    }}
                >
                    Save
                </Button>
            }
        >
            {saveState.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {v2ErrorMessage(saveState.error)}
                </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <TextField
                    size="small"
                    select
                    label="Consent"
                    value={draft.consentMode}
                    onChange={e =>
                        setDraft({
                            ...draft,
                            consentMode: e.target.value as Privacy['consentMode'],
                        })
                    }
                    sx={{ minWidth: 260 }}
                    helperText={
                        draft.consentMode === 'strict'
                            ? 'Only events with consent granted are kept (opt-in regimes).'
                            : draft.consentMode === 'required'
                              ? 'Events with consent denied are dropped; before a choice, cookieless.'
                              : 'Events without consent are counted cookieless.'
                    }
                >
                    <MenuItem value="optional">Optional (cookieless without consent)</MenuItem>
                    <MenuItem value="required">Required (drop when consent is denied)</MenuItem>
                    <MenuItem value="strict">Strict (drop unless consent is granted)</MenuItem>
                </TextField>
                <TextField
                    size="small"
                    select
                    label="GPC / Do Not Track"
                    value={draft.dnt}
                    onChange={e => setDraft({ ...draft, dnt: e.target.value as Privacy['dnt'] })}
                    sx={{ minWidth: 260 }}
                    helperText={
                        draft.dnt === 'ignore'
                            ? 'Opt-out signals are NOT honored; the evidence report says so.'
                            : undefined
                    }
                    error={draft.dnt === 'ignore'}
                >
                    <MenuItem value="cookieless">Treat as opt-out, count cookieless</MenuItem>
                    <MenuItem value="drop">Treat as opt-out, drop the events</MenuItem>
                    <MenuItem value="ignore">Ignore (not recommended)</MenuItem>
                </TextField>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                    control={toggle('suppressForwardingOnOptOut')}
                    label="Never forward opted-out events to destinations"
                />
                <FormControlLabel
                    control={toggle('scrubParams')}
                    label="Remove PII-looking parameters (emails, phone numbers)"
                />
                <FormControlLabel
                    control={toggle('cookielessVisitorHash')}
                    label="Estimate cookieless visitors with a daily-rotating hash"
                />
            </Box>
        </SectionCard>
    )
}
